import React from 'react';
import LoadingScreen from 'components/LoadingScreen';
import jwtDecode from 'jwt-decode';
import { createContext, useEffect, useReducer } from 'react';
import axios from '../utils/axios';
import PropTypes from 'prop-types';

const initialState = {
    isAuthenticated: false,
    isInitialized: false,
    user: null,
};

const isValidToken = (accessToken) => {
    if (!accessToken) return false;
    const decodedToken = jwtDecode(accessToken);
    const currentTime = Date.now() / 1000;

    return decodedToken.exp > currentTime;
};

const setUserProp = (data) => {
    localStorage.setItem('userId', data.id);
    localStorage.setItem('role', data.role);
    localStorage.setItem('email', data.email);
    localStorage.setItem('fullName', data.fullName);

    if (data.sponsor) {
        localStorage.setItem('sponsorId', data.sponsor.id);
        localStorage.setItem('sponsorName', data.sponsor.name);
    }

    localStorage.setItem('fullName', data.fullName);
};

const setSession = (accessToken) => {
    if (accessToken) {
        localStorage.setItem('accessToken', accessToken);
        axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
    } else {
        localStorage.removeItem('accessToken');
        delete axios.defaults.headers.common.Authorization;
    }
};

const reducer = (state, action) => {
    switch (action.type) {
        case 'INIT': {
            return {
                isInitialized: true,
                user: action.payload.user,
                isAuthenticated: action.payload.isAuthenticated,
            };
        }

        case 'LOGIN': {
            return {
                ...state,
                isAuthenticated: true,
                user: action.payload.user,
            };
        }

        case 'LOGOUT': {
            return { ...state, user: null, isAuthenticated: false };
        }

        case 'REGISTER': {
            return {
                ...state,
                isAuthenticated: true,
                user: action.payload.user,
            };
        }

        default: {
            return state;
        }
    }
};

const AuthContext = createContext({
    ...initialState,
    method: 'JWT',
    // logout: () => {},
    // login: (email, password) => Promise.resolve(),
    // register: (email, password, username) => Promise.resolve(),
});
export const JWTAuthProvider = ({ children }) => {
    const [state, dispatch] = useReducer(reducer, initialState);
    const login = async (email, password) => {
        const { data } = await axios.post('/api/auth/signin', {
            email,
            password,
        });
        setSession(data.accessToken);
        setUserProp(data.user);
        dispatch({
            type: 'LOGIN',
            payload: {
                user: data.user,
            },
        });
        return data;
    };

    const register = async (values) => {
        const {
            fullName,
            sponsor,
            email,
            nik,
            phoneNumber,
            registerType,
            institution,
            profession,
            perkiId,
            city,
            country,
            password,
            confirmPassword,
        } = values;
        const { data } = await axios.post('/api/auth/signup', {
            fullName,
            email,
            phoneNumber,
            nik,
            institution,
            profession,
            perkiId,
            city,
            sponsor,
            registerType,
            country,
            password,
            confirmPassword,
        });

        setSession(data.accessToken);
        setUserProp(data.user);
        dispatch({
            type: 'REGISTER',
            payload: {
                user: data.user,
            },
        });
        return data;
    };

    const removeUserProp = () => {
        localStorage.removeItem('userId');
        localStorage.removeItem('role');
        localStorage.removeItem('email');
        localStorage.removeItem('fullName');
        localStorage.removeItem('accessToken');
        localStorage.removeItem('sponsorId');
        localStorage.removeItem('sponsorName');
    };

    const logout = () => {
        removeUserProp();
        setSession(null);
        dispatch({
            type: 'LOGOUT',
        });
    };

    useEffect(() => {
        (async () => {
            try {
                const accessToken = localStorage.getItem('accessToken');

                if (accessToken && isValidToken(accessToken)) {
                    setSession(accessToken);
                    await axios
                        .get(`/api/auth/${accessToken}/profile`)
                        .then((res) => {
                            dispatch({
                                type: 'INIT',
                                payload: {
                                    user: res.data.user,
                                    isAuthenticated: true,
                                },
                            });
                        });
                } else {
                    dispatch({
                        type: 'INIT',
                        payload: {
                            ...initialState,
                        },
                    });
                }
            } catch (err) {
                console.error(err);
                dispatch({
                    type: 'INIT',
                    payload: {
                        ...initialState,
                    },
                });
            }
        })();
    }, []); // show loading until not initialized

    if (!state.isInitialized) <LoadingScreen />;

    return (
        <AuthContext.Provider
            value={{ ...state, method: 'JWT', login, register, logout }}
        >
            {children}
        </AuthContext.Provider>
    );
};

JWTAuthProvider.propTypes = {
    children: PropTypes.any,
};

export default AuthContext;
